import React, { Key, useState, ReactNode } from 'react';
import {
  useLazyQuery,
  useQuery,
  FiltersType,
  useNavigate,
} from '@common/hooks';
import {
  buildOptionsFromData,
  Currency,
  DateRangeFilter,
  ErrorHandler,
  FilteredTable,
  Gap,
  Link,
  MultiSelectTableFilter,
  Text,
  TimeWithTimeZone,
} from '@common/components';
import {
  WorkforceGroupUnitRatesListQuery,
  WORKFORCE_GROUP_FLAT_QUERY,
  WorksOrganizationUnitFilterDataQuery,
  WorksOrganizationBusinessFilterDataQuery,
  SimplifiedWorkforceGroupType,
  WorkforceGroupUnitRatesResponse,
  WorkforceGroupUnitRatesVariables,
  WorkforceGroupType,
  StaticPricingTier,
} from '@scheduling/graphql';
import Filters from 'works/legacy/lib/filters';
import { toUSFormat } from 'works/legacy/lib/date-helper';
import {
  SorterResult,
  SortOrder,
  FilterDropdownProps,
} from 'antd/lib/table/interface';
import moment from 'moment';
import { Spacing } from 'works/legacy/lib/style-helper';
import {
  OrganizationUnitType,
  OrganizationBusinessType,
} from '@institutions/graphql';
import { buildDialogUrl } from '@common/services';

const LOCALSTORAGE_FILTER_KEY = 'RateManagementOnDemandRates';

export type IncentiveRatesListConfig = {
  showWorkforceGroupColumn?: boolean;
  headerRightSlot?: ReactNode;
  filterTransform?: (filters: FiltersType) => FiltersType | undefined;
  sorterMultiples?: {
    unit: number;
    business: number;
    effectiveDate: number;
    lastUpdatedAt: number;
    priceRange: number;
  };
};

function buildViewDrawerUrlPath(
  workforceGroupId: string,
  organizationUnitId: string
) {
  return buildDialogUrl('incentive-rates-view-drawer', {
    props: {
      workforceGroupId,
      organizationUnitId,
    },
  });
}

function dateRangeFilter(
  field: string,
  props: FilterDropdownProps,
  filters: FiltersType
) {
  if (!props) return;
  return (
    <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
      <DateRangeFilter
        defaultValue={(filters?.[field] as string[])?.map((date: string) =>
          moment(date, 'YYYY-MM-DD')
        )}
        autoFocus={true}
        onSearch={(value) => {
          props.setSelectedKeys(
            value
              ? value.map((date) => moment(date).format?.('YYYY-MM-DD'))
              : []
          );
          props.confirm();
        }}
      />
    </section>
  );
}

function normalizeSortingData(
  sortingData: SorterResult<SimplifiedWorkforceGroupType>
) {
  const translateSortingData = (columnKey?: Key, order?: SortOrder) => {
    return columnKey && order ? { [columnKey as string | number]: order } : {};
  };

  return [sortingData]
    .flat()
    .map((sorterResult) =>
      translateSortingData(sorterResult.columnKey, sorterResult.order)
    )
    .reduce((current, next) => ({ ...current, ...next }), {});
}

function buildColumns(
  config: IncentiveRatesListConfig,
  selectedFilters: FiltersType,
  workforceGroupFilterData:
    | { workforceGroups?: WorkforceGroupType[] }
    | undefined,
  unitFilterData: { organizationUnits?: OrganizationUnitType[] } | undefined,
  organizationBusinessesFilterData:
    | { organizationBusinesses?: OrganizationBusinessType[] }
    | undefined,
  isStaticPricing: boolean
) {
  const defaultSorterMultiples = {
    unit: config.showWorkforceGroupColumn ? 2 : 1,
    business: config.showWorkforceGroupColumn ? 4 : 2,
    effectiveDate: config.showWorkforceGroupColumn ? 5 : 3,
    endDate: config.showWorkforceGroupColumn ? 6 : 4,
    lastUpdatedAt: config.showWorkforceGroupColumn ? 7 : 6,
    priceIncrement: config.showWorkforceGroupColumn ? 8 : 6,
  };

  const sorterMultiples = {
    ...defaultSorterMultiples,
    ...config.sorterMultiples,
  };

  return [
    ...(config.showWorkforceGroupColumn
      ? [
          {
            key: 'workforceGroup',
            title: 'Workforce Group',
            sorter: {
              multiple: 1,
            },
            filtered: selectedFilters.workforceGroup || false,
            filteredValue: selectedFilters.workforceGroup || null,
            filters: buildOptionsFromData(
              workforceGroupFilterData?.workforceGroups as WorkforceGroupType[]
            ),
            filterDropdown: MultiSelectTableFilter,
            render: (_: unknown, record: ExpandableRecord) => {
              if (record.isTierRow) return null;

              const workforceGroup = record.workforceGroup;
              const organizationUnit = record.organizationUnit;
              if (!workforceGroup || !organizationUnit) return null;

              const viewDrawerUrl = buildViewDrawerUrlPath(
                workforceGroup.id,
                organizationUnit.id
              );
              return <Link to={viewDrawerUrl}>{workforceGroup.name}</Link>;
            },
          },
        ]
      : []),
    {
      key: 'unit',
      dataIndex: ['organizationUnit'],
      title: 'Unit / Cost Center',
      render: (
        organizationUnit: OrganizationUnitType,
        record: ExpandableRecord
      ) =>
        record.isTierRow ? null : (
          <>
            <Gap vertical>
              <Text block>{organizationUnit.name}</Text>
              <Text secondary block>
                {organizationUnit.costCenter}
              </Text>
            </Gap>
          </>
        ),
      sorter: {
        multiple: sorterMultiples.unit,
      },
      filtered: selectedFilters.unit || false,
      filteredValue: selectedFilters.unit || null,
      filterDropdown: MultiSelectTableFilter,
      filters: buildOptionsFromData(
        unitFilterData?.organizationUnits || [],
        (unit: OrganizationUnitType) => ({
          text: `${unit.name} - ${unit.costCenter}`,
          value: unit.id,
        })
      ),
    },
    {
      key: 'business',
      dataIndex: ['organizationUnit', 'organizationBusiness', 'name'],
      title: 'Facility',
      sorter: {
        multiple: sorterMultiples.business,
      },
      filtered: selectedFilters.business || false,
      filteredValue: selectedFilters.business || null,
      filters: buildOptionsFromData(
        organizationBusinessesFilterData?.organizationBusinesses || []
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      key: 'effectiveDate',
      dataIndex: ['effectiveDate'],
      title: 'Start Date',
      sorter: {
        multiple: sorterMultiples.effectiveDate,
      },
      filtered: selectedFilters.effectiveDate || false,
      filteredValue: selectedFilters.effectiveDate || null,
      filterDropdown: (props: FilterDropdownProps) =>
        dateRangeFilter('effectiveDate', props, selectedFilters),
      render: (date: string) => (date ? toUSFormat(date) : ''),
    },
    {
      key: 'endDate',
      dataIndex: ['endDate'],
      title: 'End Date',
      sorter: {
        multiple: sorterMultiples.endDate,
      },
      filtered: selectedFilters.endDate || false,
      filteredValue: selectedFilters.endDate || null,
      filterDropdown: (props: FilterDropdownProps) =>
        dateRangeFilter('endDate', props, selectedFilters),
      render: (date: string) => (date ? toUSFormat(date) : ''),
    },
    ...(isStaticPricing
      ? [
          {
            key: 'payRateRange',
            title: 'Pay Rate Range',
            render: (record: {
              isTierRow?: boolean;
              tierLabel?: string;
              tierRange?: string;
              tierPayRate?: number;
              pricingMode?: string;
              displayAsLumpSum?: boolean;
              minPrice: number;
              maxPrice: number;
              staticPricingTiers?: {
                payRate: number;
              }[];
            }) => {
              if (record.isTierRow) {
                return (
                  <Gap vertical size="xs">
                    <Text>{record.tierLabel}</Text>
                    <Text>
                      {record.tierRange} |{' '}
                      <Currency value={record.tierPayRate || 0} />
                      /hr
                    </Text>
                  </Gap>
                );
              }

              if (
                record.pricingMode === 'STATIC' &&
                record.staticPricingTiers?.length
              ) {
                const rates = record.staticPricingTiers.map((t) => t.payRate);
                const min = Math.min(...rates);
                const max = Math.max(...rates);
                const tierCount = record.staticPricingTiers.length;
                return (
                  <Gap vertical>
                    <Text block>
                      <Currency value={min} /> - <Currency value={max} />
                      /hr
                    </Text>
                    <Text secondary block>
                      {tierCount} {tierCount === 1 ? 'tier' : 'tiers'}
                    </Text>
                  </Gap>
                );
              }
              return (
                <>
                  <Currency value={record.minPrice} /> -{' '}
                  <Currency value={record.maxPrice} />
                </>
              );
            },
          },
        ]
      : [
          {
            key: 'priceRange',
            title: 'Price Range',
            render: (record: { priceRange: number }) => (
              <Currency value={record.priceRange} />
            ),
          },
          {
            key: 'priceIncrement',
            title: 'Price Increment',
            render: (record: { incentiveIncrement: number }) => (
              <Currency value={record.incentiveIncrement} />
            ),
          },
          {
            key: 'rateRange',
            title: 'Rate Range',
            render: (record: { minPrice: number; maxPrice: number }) => (
              <>
                <Currency value={record.minPrice} /> -{' '}
                <Currency value={record.maxPrice} />
              </>
            ),
          },
        ]),
    {
      key: 'createdBy',
      dataIndex: ['createdBy', 'name'],
      title: 'Updated by',
      render: (name: string) => <Text>{name}</Text>,
    },
    {
      key: 'lastUpdatedAt',
      dataIndex: ['lastUpdatedAt'],
      title: 'Last Update',
      render: (date: string) =>
        date && <TimeWithTimeZone date={date} variant="long_format" />,
    },
  ];
}

type ExpandableRecord = {
  id: string;
  isTierRow?: boolean;
  children?: ExpandableRecord[];
  pricingMode?: string;
  displayAsLumpSum?: boolean;
  staticPricingTiers?: StaticPricingTier[];
  tierLabel?: string;
  tierRange?: string;
  tierPayRate?: number;
  workforceGroup?: SimplifiedWorkforceGroupType;
  organizationUnit?: OrganizationUnitType;
};

function fillRateRange(sorted: StaticPricingTier[], index: number) {
  const lower = index > 0 ? sorted[index - 1].fillRateThreshold : 0;
  return `${lower}% - ${sorted[index].fillRateThreshold}%`;
}

export function IncentiveRatesList({
  workforceGroupId,
  onFiltersChange,
  localStorageFilterKey,
  config = {},
}: {
  workforceGroupId?: string;
  onFiltersChange?: (filters: FiltersType) => void;
  localStorageFilterKey?: string;
  config?: IncentiveRatesListConfig;
}) {
  const [fetchUnits, { data: unitFilterData, error: unitsError }] =
    useLazyQuery(WorksOrganizationUnitFilterDataQuery, {
      variables: {
        input: {
          scope: 'department',
        },
      },
    });
  const [
    fetchWorkforceGroup,
    { data: workforceGroupFilterData, error: wfgError },
  ] = useLazyQuery(WORKFORCE_GROUP_FLAT_QUERY);
  const [
    fetchOrgBusinesses,
    { data: organizationBusinessesFilterData, error: businessError },
  ] = useLazyQuery(WorksOrganizationBusinessFilterDataQuery);

  const filtersDataReady =
    !!unitFilterData &&
    !!workforceGroupFilterData &&
    !!organizationBusinessesFilterData;

  const navigate = useNavigate();

  const filterKey = localStorageFilterKey || LOCALSTORAGE_FILTER_KEY;

  const [selectedFilters, setSelectedFilters] = useState(() => {
    return Filters.getFilters(filterKey);
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const onFilterChange = (newFilters: Record<string, string[] | null>) => {
    delete newFilters['unitName'];
    delete newFilters['clinicalUnit'];
    delete newFilters['costCenter'];

    setSelectedFilters(newFilters);
    Filters.setFilters(filterKey, newFilters);

    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const { loading, error, fetchMore, data } = useQuery<
    WorkforceGroupUnitRatesResponse,
    WorkforceGroupUnitRatesVariables
  >(WorkforceGroupUnitRatesListQuery, {
    variables: {
      input: {
        filters: {
          ...selectedFilters,
          ...(workforceGroupId && { workforceGroup: [workforceGroupId] }),
        },
      },
    },
    onCompleted() {
      fetchUnits();
      fetchOrgBusinesses();
      fetchWorkforceGroup();
    },
  });

  function handleTableChange(
    pagination: { current: number; pageSize: number },
    filters: Record<string, Key[] | null>,
    sortingData: SorterResult<SimplifiedWorkforceGroupType>
  ) {
    fetchMore({
      variables: {
        input: {
          pagination: {
            page: pagination.current,
            pageSize: pagination.pageSize,
          },
          orders: {
            ...normalizeSortingData(sortingData),
          },
          filters: {
            ...filters,
            ...(workforceGroupId && { workforceGroup: [workforceGroupId] }),
          },
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        return fetchMoreResult;
      },
    });
  }

  const anyError = error || unitsError || wfgError || businessError;

  if (anyError) {
    return <ErrorHandler error={anyError} log="IncentiveRatesList" />;
  }

  const totalPaginationCount =
    data?.workforceGroupUnitRatesList?.metadata?.totalCount;

  const workforceGroupUnitRatesList =
    data?.workforceGroupUnitRatesList.collection || [];

  const isStaticPricing = workforceGroupUnitRatesList.some(
    (record) => record.pricingMode === 'STATIC'
  );

  const columns = buildColumns(
    config,
    selectedFilters,
    {
      workforceGroups:
        workforceGroupFilterData?.workforceGroups as WorkforceGroupType[],
    },
    unitFilterData,
    organizationBusinessesFilterData,
    isStaticPricing
  );
  const workforceGroupUnitRatesTableData: ExpandableRecord[] =
    workforceGroupUnitRatesList.map((record) => {
      if (
        record.pricingMode !== 'STATIC' ||
        !record.staticPricingTiers?.length
      ) {
        return record;
      }

      const sorted = [...record.staticPricingTiers].sort(
        (a, b) => a.fillRateThreshold - b.fillRateThreshold
      );

      const children = sorted.map((tier, index) => ({
        id: `${record.id}-tier-${tier.id}`,
        isTierRow: true,
        pricingMode: 'STATIC',
        displayAsLumpSum: record.displayAsLumpSum,
        tierLabel: tier.label,
        tierRange: fillRateRange(sorted, index),
        tierPayRate: tier.payRate,
      }));

      return {
        ...record,
        children,
      };
    });

  function onExpand(expanded: boolean, key: string) {
    setExpandedRowKeys((currentKeys) => {
      if (expanded) {
        return [...currentKeys, key];
      }

      return currentKeys.filter((currentKey) => currentKey !== key);
    });
  }

  return (
    <>
      <section>
        <FilteredTable
          headerRightSlot={config.headerRightSlot}
          filtersDataReady={filtersDataReady}
          className="incentive-rates-list"
          rowKey="id"
          columns={columns}
          dataSource={workforceGroupUnitRatesTableData}
          loading={loading}
          onChange={handleTableChange}
          scroll={isStaticPricing ? {} : undefined}
          onRow={(record: ExpandableRecord) =>
            record.isTierRow
              ? {}
              : {
                  onClick: () => {
                    if (
                      !record.workforceGroup?.id ||
                      !record.organizationUnit?.id
                    )
                      return;

                    const viewDrawerUrl = buildViewDrawerUrlPath(
                      record.workforceGroup.id,
                      record.organizationUnit.id
                    );

                    navigate(viewDrawerUrl);
                  },
                }
          }
          expandable={
            isStaticPricing
              ? {
                  expandRowByClick: false,
                  expandedRowKeys,
                  indentSize: 0,
                  onExpand: (expanded: boolean, record: ExpandableRecord) =>
                    onExpand(expanded, record.id),
                  rowExpandable: (record: ExpandableRecord) =>
                    !record.isTierRow && !!record.children?.length,
                }
              : undefined
          }
          rowClassName={(record: ExpandableRecord) =>
            record.isTierRow ? 'static-tier-child-row' : ''
          }
          pagination={{
            total: totalPaginationCount,
          }}
          filters={
            config.filterTransform
              ? config.filterTransform(selectedFilters)
              : selectedFilters
          }
          localStorageFilterKey={filterKey}
          onFilterChange={onFilterChange}
        />
      </section>
    </>
  );
}
